import React, { useEffect, useState } from "react";
import { Button, Typography } from "@mui/material";
import { showApiError, showApiSuccess } from "../framework/formUtils";
import { getOverlappingWorkShiftTimeSlots, saveWorkShiftTimeSlot } from "../../services/workTimeBetaService";
import { WorkShiftTimeSlotItem } from "../../models/workShitTimeSlot/workShiftTimeSlotItem";
import { useParams } from "react-router-dom";
import { IWorkTimeDetailsPage } from "./workTimeBetaDetailsPageMain";
import { Base } from "../../framework/base";
import { CompactTimeline } from "./compactTimeline";
import { WorkTimeSaveData } from "../../models/workShitTimeSlot/workTimeSaveData";
import { AppUtils } from "../../models/common/appUtils";
import { OverlappingSlotsQueryData } from "../../models/workShitTimeSlot/overlappingSlotsQueryData";
import { EditDialog } from "../framework/editDialog";
import { useAppDispatch } from "../../framework/customStore";
import * as storeActions from "../../models/store/storeActions";
import { ConfirmationDialogType } from "../../models/store/storeTypes";
import { useTranslation } from "react-i18next";

interface IOverlappingSaveDialogProps {
    open: boolean;
    onClose: () => void;
    saveData: WorkTimeSaveData;
    onSaveSuccess: (shiftId: string) => void;
}

export const OverlappingSaveDialog = ({ open, onClose, saveData, onSaveSuccess }: IOverlappingSaveDialogProps) => {
    const [overlappingSlots, setOverlappingSlots] = useState<WorkShiftTimeSlotItem[]>();
    const { date } = useParams<IWorkTimeDetailsPage>();
    const { t } = useTranslation();
    const dispatch = useAppDispatch();

    const handleClose = () => {
        onClose();
    };

    const calculateDuration = (startDate, endDate, short=false) => {
        const totalMinutes = Base.dateDiffInMinutes(new Date(startDate), new Date(endDate));
        if (short) return AppUtils.getDurationStrByDurationMinShort(totalMinutes);
        return AppUtils.getDurationStrByDurationMin(totalMinutes);
    };

    const formatTime = (time: string) =>
        Base.isSameDay(Base.dateStrToDayjsIgnoreTimezone(time), date)
            ? Base.dateStrToOriginalTimezoneTimeStr(time)
            : Base.dateStrToOriginalTimezoneDateTimeStr(time);

    const formatDates = (slot: WorkShiftTimeSlotItem) => {
        return `${formatTime(slot.startDate)} - ${slot.endDate ? formatTime(slot.endDate) : ""} (UTC ${Base.dateTzOffset(slot.startDate, slot.timeZoneName)})`;
    };

    const handleConfirmShiftOverwrite = () => {
        dispatch(
            storeActions.setConfirmation(
                ConfirmationDialogType.Warning,
                t("common.warning"),
                t("workTime.confirmSaveForModifiedShift"),
                () => {
                    dispatch(storeActions.clearConfirmation());
                    handleOverwrite(true);
                },
                () => {
                    dispatch(storeActions.clearConfirmation());
                }
            )
        );
    };

    const handleOverwrite = (allowShiftOverwrite = false) => {
        saveWorkShiftTimeSlot(
            saveData,
            true,
            true,
            allowShiftOverwrite
        )
            .then((result) => {
                showApiSuccess(t("common.saveSuccess"));
                onSaveSuccess(result.workShiftId);
                handleClose();
            })
            .catch((error) => {
                if (
                    error.message === t("workShift.dateHasBeenModified")
                ) {
                    handleConfirmShiftOverwrite();
                } else {
                    showApiError(error);
                }
            });
    };

    const fetchOverLappingSlots = async() => {
        if (saveData && open && saveData.startDate && saveData.endDate) {
            try {
                const queryData: OverlappingSlotsQueryData = {
                    startDate: saveData.startDate,
                    endDate: saveData.endDate,
                    timeZoneName: saveData.timeZoneName,
                    employeeId: saveData.employeeId
                };
                const slots = await getOverlappingWorkShiftTimeSlots(queryData);
                setOverlappingSlots(slots.filter(s => s.id !== saveData.id));
            } catch (error) {
                showApiError(AppUtils.translate("workTime.overlappingFetchFailed"));
            }
        }
    };

    useEffect(() => {
        void fetchOverLappingSlots();
    }, [open, saveData]);

    const primaryActions = [
        <Button
            key={"save"}
            onClick={() => handleOverwrite()}
            color="success"
            variant="contained"
            fullWidth
        >
            {AppUtils.translate("workTime.saveOverwriting")}
        </Button>,
        <Button
            key={"cancel"}
            onClick={handleClose}
            variant="outlined"
            color="success"
            fullWidth
        >
            {AppUtils.translate("common.cancelSave")}
        </Button>,
    ];

    return (
        saveData && (
            <>
                <EditDialog
                    open={open}
                    onClose={handleClose}
                    title={AppUtils.translate(
                        "workTime.conflictingEntriesNotAllowed"
                    )}
                    body={
                        <>
                            <Typography gutterBottom variant="subtitle2">
                                {AppUtils.translate("workTime.overlapDetected")}
                                :
                            </Typography>
                            {overlappingSlots?.map((ds) => (
                                <Typography
                                    key={ds.id}
                                    color="error"
                                    gutterBottom
                                    variant="subtitle2"
                                >
                                    {formatDates(ds)}
                                </Typography>
                            ))}
                            {overlappingSlots && (
                                <CompactTimeline
                                    overlappingSlots={overlappingSlots}
                                    newSlot={saveData}
                                    formatTime={formatTime}
                                    calculateDuration={calculateDuration}
                                />
                            )}
                        </>
                    }
                    primaryActions={primaryActions}
                />
            </>
        )
    );
};
